// 템플릿 파일을 로드하고 placeholder를 치환하는 유틸리티 함수들

export const loadTemplate = async (templateName) => {
  try {
    // public 폴더에서 템플릿 파일을 가져오는 방식으로 변경
    // 개발환경에서는 /templates/ 경로로 접근
    const response = await fetch(`/templates/${templateName}`);
    if (!response.ok) {
      throw new Error(`템플릿 파일을 불러올 수 없습니다: ${templateName}`);
    }
    return await response.text();
  } catch (error) {
    console.error('템플릿 로딩 오류:', error);
    // 폴백: 인라인 템플릿 사용
    return getInlineTemplate(templateName);
  }
};

// placeholder를 실제 데이터로 치환하는 함수
export const replacePlaceholders = (template, data) => {
  let result = template;
  
  // 각 placeholder를 실제 값으로 치환
  Object.keys(data).forEach(key => {
    let value;
    
    // WORD_DATA의 경우 특별 처리
    if (key === 'WORD_DATA' && Array.isArray(data[key])) {
      value = JSON.stringify(data[key]);
    } else if (typeof data[key] === 'object') {
      value = JSON.stringify(data[key]);
    } else {
      value = String(data[key]);
    }
    
    // 공백이 있는 버전과 없는 버전 모두 처리
    const placeholders = [
      `{{${key}}}`,           // 공백 없음: {{WORD_DATA}}
      `{{ ${key} }}`,         // 공백 있음: {{ WORD_DATA }}
      `{{  ${key}  }}`,       // 양쪽 공백: {{  WORD_DATA  }}
    ];
    
    placeholders.forEach(placeholder => {
      // 정규식에서 특수문자 escape
      const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      result = result.replace(new RegExp(escapedPlaceholder, 'g'), value);
    });
  });
  
  return result;
};

// 객관식 퀴즈 데이터를 생성하는 함수
const generateQuizData = (words) => {
  if (!words || words.length === 0) return [];
  
  return words.map((word) => {
    // 현재 단어 제외한 나머지 단어들에서 오답 선택지 생성
    const otherWords = words.filter(w => w.english !== word.english);
    const shuffledOthers = [...otherWords].sort(() => Math.random() - 0.5);
    const wrongAnswers = shuffledOthers.slice(0, 3).map(w => w.korean);
    
    // 정답과 오답 합쳐서 섞기
    const allChoices = [word.korean, ...wrongAnswers];
    const shuffledChoices = allChoices.sort(() => Math.random() - 0.5);

    return {
      english: word.english,
      correctAnswer: word.korean,
      choices: shuffledChoices
    };
  });
};

// 주관식 퀴즈 HTML 생성 함수
// 문항생성 HTML 생성 함수
export const generateQuestionGenerationHTML = async (questions, passage, settings, sheetUrl = '') => {
  console.log('generateQuestionGenerationHTML 호출됨');
  console.log('문항 데이터:', questions);
  console.log('지문:', passage?.substring(0, 100));
  console.log('설정:', settings);
  
  // 문항 HTML 생성
  const questionsHTML = questions.map((question, index) => {
    const difficultyClass = `difficulty-${question.difficulty.toLowerCase()}`;
    
    // 선택지에서 A., B., C., D. 제거 (이미 포함되어 있을 수 있으므로)
    const options = question.choices || question.options || [];
    const cleanOptions = options.map(option => {
      // A., B., C., D.로 시작하는 경우 제거
      return option.replace(/^[A-D]\.\s*/, '').trim();
    });
    
    const optionsHTML = cleanOptions.map((option, optionIndex) => 
      `<div class="option" onclick="selectOption(${index}, ${optionIndex})">${String.fromCharCode(65 + optionIndex)}. ${option}</div>`
    ).join('');
    
    // 해설에 예상 정답률 정보 추가
    const explanationText = question.explanation || '해설이 제공되지 않았습니다.';
    const expectedAccuracy = question.expectedAccuracy || question.expected_accuracy || question.expected_correct_rate;
    const accuracyInfo = expectedAccuracy ? `<br><br>📊 <strong>예상 정답률:</strong> ${expectedAccuracy}` : '';
    
    return `
      <div class="question-card" id="question-${index}">
        <div class="question-header">
          <span class="question-number">문제 ${index + 1}</span>
          <span class="difficulty-badge ${difficultyClass}">${question.difficulty}</span>
        </div>
        <div class="question-text">${question.question}</div>
        <div class="options">
          ${optionsHTML}
        </div>
        <div class="explanation">
          <div class="explanation-title">💡 해설</div>
          <div>${explanationText}${accuracyInfo}</div>
        </div>
      </div>
    `;
  }).join('');
  
  // JavaScript용 문항 데이터 생성
  const questionsData = questions.map((question, questionIndex) => {
    // correctIndex 처리 - 다양한 형태에 대응
    let correctIndex = 0;
    
    if (question.correctIndex !== undefined && typeof question.correctIndex === 'number') {
      // GPT가 correctIndex로 직접 응답한 경우 (가장 우선)
      correctIndex = question.correctIndex;
    } else if (question.correct_answer !== undefined) {
      if (typeof question.correct_answer === 'string') {
        // "A", "B", "C", "D" 형식인 경우
        const upperAnswer = question.correct_answer.toUpperCase();
        if (['A', 'B', 'C', 'D'].includes(upperAnswer)) {
          correctIndex = upperAnswer.charCodeAt(0) - 65; // A=0, B=1, C=2, D=3
        }
      } else if (typeof question.correct_answer === 'number') {
        correctIndex = question.correct_answer;
      }
    }
    
    // correctIndex가 유효 범위(0-3)를 벗어나는 경우 0으로 설정
    if (correctIndex < 0 || correctIndex > 3) {
      console.warn(`문제 ${questionIndex + 1}: correctIndex(${correctIndex})가 유효하지 않습니다. 0으로 설정합니다.`);
      correctIndex = 0;
    }
    
    console.log(`문제 ${questionIndex + 1} correctIndex 처리:`, {
      원본: question.correctIndex || question.correct_answer,
      변환결과: correctIndex,
      옵션수: (question.choices || question.options)?.length,
      정답텍스트: question.answer
    });
    
    return {
      question: question.question,
      options: question.options,
      correctIndex: correctIndex,
      explanation: question.explanation,
      difficulty: question.difficulty
    };
  });
  
  // 지문 미리보기 생성 (안전한 길이로 제한)
  const passagePreview = (passage || '').substring(0, 50).replace(/"/g, '&quot;').replace(/'/g, '&#x27;') + '...';
  
  const templateData = {
    TITLE: `문항생성 - ${settings.schoolLevel} ${settings.grade}학년`,
    PASSAGE: (passage || '').replace(/"/g, '&quot;').replace(/'/g, '&#x27;'),
    PASSAGE_PREVIEW: passagePreview,
    PASSAGE_ID: settings.passageId || 'local_generated',
    TOTAL_QUESTIONS: questions.length,
    QUESTIONS_HTML: questionsHTML,
    QUESTIONS_DATA: JSON.stringify(questionsData).replace(/</g, '\\u003c').replace(/>/g, '\\u003e'),
    SCHOOL_LEVEL: settings.schoolLevel,
    GRADE: settings.grade,
    SHEET_URL: sheetUrl || ''
  };
  
  console.log('문항생성 템플릿 데이터:', templateData);
  
  try {
    console.log('문항생성 템플릿 로딩 시도 중...');
    const template = await loadTemplate('question-generation-template.html');
    console.log('문항생성 템플릿 로딩 성공, 길이:', template.length);
    
    const result = replacePlaceholders(template, templateData);
    console.log('문항생성 placeholder 치환 완료, 최종 길이:', result.length);
    
    return result;
  } catch (error) {
    console.error('문항생성 템플릿 생성 오류:', error);
    throw error;
  }
};

export const generateShortAnswerQuizHTML = async (words, settings, sheetUrl = '') => {
  console.log('generateShortAnswerQuizHTML 호출됨');
  console.log('단어 데이터:', words);
  console.log('설정:', settings);
  console.log('시트 URL:', sheetUrl);
  
  // 주관식 퀴즈는 단순히 단어와 뜻만 필요
  const quizData = words.map(word => ({
    english: word.english,
    korean: word.korean
  }));
  
  const templateData = {
    QUIZ_DATA: quizData,
    TOTAL_QUESTIONS: words.length,
    SCHOOL_LEVEL: settings.schoolLevel,
    GRADE: settings.grade,
    WORD_COUNT: settings.wordCount,
    SHEET_URL: sheetUrl || ''
  };
  
  console.log('주관식 퀴즈 템플릿 데이터:', templateData);
  
  try {
    console.log('주관식 퀴즈 템플릿 로딩 시도 중...');
    const template = await loadTemplate('short-answer-quiz-template.html');
    console.log('주관식 퀴즈 템플릿 로딩 성공, 길이:', template.length);
    
    const result = replacePlaceholders(template, templateData);
    console.log('주관식 퀴즈 placeholder 치환 완료, 최종 길이:', result.length);
    
    return result;
  } catch (error) {
    console.error('주관식 퀴즈 HTML 생성 오류:', error);
    console.log('주관식 퀴즈 폴백 모드로 전환...');
    // 폴백: 인라인 템플릿 사용
    return getInlineShortAnswerQuizHTML(words, settings);
  }
};

// 객관식 퀴즈 HTML 생성 함수
export const generateMultipleChoiceQuizHTML = async (words, settings, sheetUrl = '') => {
  console.log('generateMultipleChoiceQuizHTML 호출됨');
  console.log('단어 데이터:', words);
  console.log('설정:', settings);
  console.log('시트 URL:', sheetUrl);
  
  const quizData = generateQuizData(words);
  
  const templateData = {
    QUIZ_DATA: quizData,
    TOTAL_QUESTIONS: words.length,
    SCHOOL_LEVEL: settings.schoolLevel,
    GRADE: settings.grade,
    WORD_COUNT: settings.wordCount,
    SHEET_URL: sheetUrl || ''
  };
  
  console.log('퀴즈 템플릿 데이터:', templateData);
  
  try {
    console.log('퀴즈 템플릿 로딩 시도 중...');
    const template = await loadTemplate('multiple-choice-quiz-template.html');
    console.log('퀴즈 템플릿 로딩 성공, 길이:', template.length);
    
    const result = replacePlaceholders(template, templateData);
    console.log('퀴즈 placeholder 치환 완료, 최종 길이:', result.length);
    
    return result;
  } catch (error) {
    console.error('객관식 퀴즈 HTML 생성 오류:', error);
    console.log('퀴즈 폴백 모드로 전환...');
    // 폴백: 인라인 템플릿 사용
    return getInlineMultipleChoiceQuizHTML(words, settings);
  }
};

// 플래시카드 HTML 생성 함수
export const generateFlashCardHTML = async (words, settings, sheetUrl = '') => {
  console.log('generateFlashCardHTML 호출됨');
  console.log('단어 데이터:', words);
  console.log('설정:', settings);
  console.log('시트 URL:', sheetUrl);
  
  const templateData = {
    WORD_DATA: words,
    TOTAL_WORDS: words.length,
    SCHOOL_LEVEL: settings.schoolLevel,
    GRADE: settings.grade,
    WORD_COUNT: settings.wordCount,
    SHEET_URL: sheetUrl || ''
  };
  
  console.log('템플릿 데이터:', templateData);
  
  try {
    console.log('템플릿 로딩 시도 중...');
    const template = await loadTemplate('flashcard-template.html');
    console.log('템플릿 로딩 성공, 길이:', template.length);
    
    const result = replacePlaceholders(template, templateData);
    console.log('placeholder 치환 완료, 최종 길이:', result.length);
    
    return result;
  } catch (error) {
    console.error('플래시카드 HTML 생성 오류:', error);
    console.log('폴백 모드로 전환...');
    // 폴백: 기존 인라인 방식 사용
    return getInlineFlashCardHTML(words, settings);
  }
};

// 폴백용 인라인 템플릿 (개발 편의성을 위해)
const getInlineTemplate = (templateName) => {
  if (templateName === 'short-answer-quiz-template.html') {
    return `<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>주관식 영어 단어 퀴즈</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .container { background: white; border-radius: 20px; padding: 30px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); max-width: 600px; width: 100%; }
        .title { color: #333; margin-bottom: 30px; font-size: 24px; font-weight: bold; text-align: center; }
        .name-input { margin-bottom: 30px; text-align: center; }
        .name-input input { padding: 15px; border: 2px solid #e1e5e9; border-radius: 10px; font-size: 16px; width: 100%; max-width: 300px; margin-bottom: 15px; }
        .progress-section { margin-bottom: 20px; }
        .progress-info { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .question-counter { font-size: 14px; color: #666; }
        .score-display { font-size: 14px; color: #4facfe; font-weight: bold; }
        .progress { background: #e1e5e9; border-radius: 10px; height: 8px; overflow: hidden; }
        .progress-bar { background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%); height: 100%; transition: width 0.3s ease; }
        .question-card { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); border-radius: 15px; padding: 40px 30px; margin: 30px 0; color: white; text-align: center; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
        .question-title { font-size: 18px; margin-bottom: 20px; opacity: 0.9; }
        .english-word { font-size: 36px; font-weight: bold; margin-bottom: 20px; }
        .sound-btn { background: rgba(255,255,255,0.2); border: none; border-radius: 50%; width: 60px; height: 60px; color: white; cursor: pointer; font-size: 24px; transition: all 0.3s; }
        .sound-btn:hover { background: rgba(255,255,255,0.3); transform: scale(1.1); }
        .answer-section { margin: 30px 0; }
        .answer-input { width: 100%; padding: 15px 20px; font-size: 18px; border: 2px solid #e1e5e9; border-radius: 10px; text-align: center; transition: all 0.3s; }
        .answer-input:focus { outline: none; border-color: #4facfe; box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.1); }
        .answer-input.correct { border-color: #4CAF50; background: #e8f5e8; color: #2e7d32; }
        .answer-input.wrong { border-color: #f44336; background: #ffeaea; color: #c62828; }
        .answer-input.disabled { background: #f5f5f5; cursor: not-allowed; }
        .feedback-section { margin: 20px 0; padding: 15px; border-radius: 10px; text-align: center; font-weight: bold; }
        .feedback-section.correct { background: #e8f5e8; color: #2e7d32; border: 2px solid #4CAF50; }
        .feedback-section.wrong { background: #ffeaea; color: #c62828; border: 2px solid #f44336; }
        .correct-answer { margin-top: 10px; font-size: 16px; color: #4CAF50; }
        .hint-section { margin: 20px 0; text-align: center; }
        .hint-btn { background: #ffa726; color: white; border: none; padding: 8px 16px; border-radius: 20px; font-size: 14px; cursor: pointer; transition: all 0.3s; }
        .hint-btn:hover { background: #ff9800; }
        .hint-btn:disabled { background: #ccc; cursor: not-allowed; }
        .hint-text { margin-top: 10px; padding: 10px; background: #fff3e0; border-radius: 8px; color: #f57c00; font-size: 14px; }
        .btn { padding: 15px 30px; border: none; border-radius: 10px; font-size: 16px; font-weight: bold; cursor: pointer; transition: all 0.3s; min-width: 120px; }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .check-btn { background: #4facfe; color: white; width: 100%; margin-top: 20px; }
        .check-btn:hover:not(:disabled) { background: #3b9cfe; transform: translateY(-2px); }
        .next-btn { background: #4CAF50; color: white; width: 100%; margin-top: 10px; }
        .next-btn:hover:not(:disabled) { background: #45a049; transform: translateY(-2px); }
        .start-btn, .retry-btn { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 40px; font-size: 18px; }
        .start-btn:hover, .retry-btn:hover { transform: translateY(-2px); }
        .results-screen { text-align: center; }
        .final-score { font-size: 48px; font-weight: bold; color: #4facfe; margin: 20px 0; }
        .accuracy { font-size: 24px; color: #666; margin-bottom: 30px; }
        .stats-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 20px; margin: 30px 0; }
        .stat-card { background: #f8f9fa; padding: 20px; border-radius: 10px; text-align: center; }
        .stat-number { font-size: 24px; font-weight: bold; color: #4facfe; margin-bottom: 5px; }
        .stat-label { color: #666; font-size: 14px; }
        .results-list { max-height: 400px; overflow-y: auto; margin: 30px 0; text-align: left; }
        .result-item { padding: 15px; margin: 10px 0; border-radius: 10px; border: 2px solid; }
        .result-item.correct { border-color: #4CAF50; background: #e8f5e8; }
        .result-item.wrong { border-color: #f44336; background: #ffeaea; }
        .result-word { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
        .result-answer { font-size: 14px; color: #666; }
        .result-correct-answer { color: #4CAF50; font-weight: bold; }
        .result-wrong-answer { color: #f44336; }
        .buttons-container { display: flex; gap: 15px; justify-content: center; margin-top: 30px; }
        .hidden { display: none; }
        .shake { animation: shake 0.5s ease-in-out; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        @media (max-width: 480px) { .container { padding: 20px; margin: 10px; } .english-word { font-size: 28px; } .answer-input { font-size: 16px; padding: 12px 15px; } .buttons-container { flex-direction: column; gap: 10px; } .btn { width: 100%; } .stats-grid { grid-template-columns: 1fr; gap: 10px; } }
    </style>
</head>
<body>
    <div class="container">
        <div id="startScreen">
            <h1 class="title">주관식 영어 단어 퀴즈</h1>
            <p style="text-align: center; color: #666; margin-bottom: 30px;">{{TOTAL_QUESTIONS}}개의 단어를 직접 입력해보세요!</p>
            <div class="name-input">
                <input type="text" id="learnerName" placeholder="이름을 입력하세요" maxlength="20"><br>
                <button class="btn start-btn" onclick="startQuiz()">퀴즈 시작</button>
            </div>
        </div>
        <div id="quizScreen" class="hidden">
            <div class="progress-section">
                <div class="progress-info"><span class="question-counter" id="questionCounter">1 / {{TOTAL_QUESTIONS}}</span><span class="score-display" id="scoreDisplay">점수: 0</span></div>
                <div class="progress"><div class="progress-bar" id="progressBar"></div></div>
            </div>
            <div class="question-card">
                <div class="question-title">다음 단어의 뜻을 한글로 입력하세요</div>
                <div class="english-word" id="englishWord"></div>
                <button class="sound-btn" onclick="playSound()">🔊</button>
            </div>
            <div class="answer-section"><input type="text" class="answer-input" id="answerInput" placeholder="한글로 답을 입력하세요..." autocomplete="off" spellcheck="false"></div>
            <div class="hint-section"><button class="hint-btn" id="hintBtn" onclick="showHint()">힌트 보기</button><div class="hint-text hidden" id="hintText"></div></div>
            <div class="feedback-section hidden" id="feedbackSection"><div id="feedbackMessage"></div><div class="correct-answer hidden" id="correctAnswerDisplay"></div></div>
            <button class="btn check-btn" id="checkBtn" onclick="checkAnswer()">정답 확인</button>
            <button class="btn next-btn hidden" id="nextBtn" onclick="nextQuestion()">다음 문제</button>
        </div>
        <div id="resultsScreen" class="hidden">
            <div class="results-screen">
                <h2 class="title">퀴즈 완료!</h2>
                <div class="final-score" id="finalScore">0 / 0</div>
                <div class="accuracy" id="finalAccuracy">정답률: 0%</div>
                <div class="stats-grid">
                    <div class="stat-card"><div class="stat-number" id="correctCount">0</div><div class="stat-label">정답</div></div>
                    <div class="stat-card"><div class="stat-number" id="wrongCount">0</div><div class="stat-label">오답</div></div>
                    <div class="stat-card"><div class="stat-number" id="hintUsedCount">0</div><div class="stat-label">힌트 사용</div></div>
                </div>
                <div class="results-list" id="resultsList"></div>
                <div class="buttons-container"><button class="btn retry-btn" onclick="restartQuiz()">다시 풀기</button></div>
                <p style="margin-top: 30px; color: #666; font-size: 14px;">학습 로그가 콘솔에 출력되었습니다.</p>
            </div>
        </div>
    </div>
    <script>
        const quizData = {{QUIZ_DATA}};
        let currentQuestionIndex = 0, score = 0, userAnswers = [], learnerName = '', startTime = new Date(), hintUsedCount = 0, currentHintUsed = false;
        function startQuiz() { learnerName = document.getElementById('learnerName').value.trim(); if (!learnerName) { alert('이름을 입력해주세요.'); return; } document.getElementById('startScreen').classList.add('hidden'); document.getElementById('quizScreen').classList.remove('hidden'); currentQuestionIndex = 0; score = 0; userAnswers = []; hintUsedCount = 0; startTime = new Date(); showQuestion(); }
        function showQuestion() { if (currentQuestionIndex >= quizData.length) { showResults(); return; } const question = quizData[currentQuestionIndex]; currentHintUsed = false; resetQuestionUI(); const progress = ((currentQuestionIndex + 1) / quizData.length) * 100; document.getElementById('progressBar').style.width = progress + '%'; document.getElementById('questionCounter').textContent = \`\${currentQuestionIndex + 1} / \${quizData.length}\`; document.getElementById('scoreDisplay').textContent = \`점수: \${score}\`; document.getElementById('englishWord').textContent = question.english; document.getElementById('answerInput').focus(); }
        function resetQuestionUI() { const answerInput = document.getElementById('answerInput'); const feedbackSection = document.getElementById('feedbackSection'); const hintSection = document.getElementById('hintText'); const hintBtn = document.getElementById('hintBtn'); const checkBtn = document.getElementById('checkBtn'); const nextBtn = document.getElementById('nextBtn'); answerInput.value = ''; answerInput.disabled = false; answerInput.className = 'answer-input'; feedbackSection.classList.add('hidden'); hintSection.classList.add('hidden'); hintBtn.disabled = false; hintBtn.textContent = '힌트 보기'; checkBtn.classList.remove('hidden'); nextBtn.classList.add('hidden'); }
        function showHint() { if (currentHintUsed) return; const question = quizData[currentQuestionIndex]; const hint = generateHint(question.korean); document.getElementById('hintText').textContent = \`힌트: \${hint}\`; document.getElementById('hintText').classList.remove('hidden'); document.getElementById('hintBtn').disabled = true; document.getElementById('hintBtn').textContent = '힌트 사용됨'; currentHintUsed = true; }
        function generateHint(answer) { if (answer.length <= 2) { return answer.charAt(0) + '*'; } const firstChar = answer.charAt(0); const lastChar = answer.charAt(answer.length - 1); const middleLength = answer.length - 2; const maskedMiddle = '*'.repeat(middleLength); return firstChar + maskedMiddle + lastChar; }
        function checkAnswer() { const userAnswer = document.getElementById('answerInput').value.trim(); const question = quizData[currentQuestionIndex]; const correctAnswer = question.korean; if (!userAnswer) { alert('답을 입력해주세요.'); return; } const isCorrect = checkAnswerSimilarity(userAnswer, correctAnswer); const answerInput = document.getElementById('answerInput'); const feedbackSection = document.getElementById('feedbackSection'); const feedbackMessage = document.getElementById('feedbackMessage'); const correctAnswerDisplay = document.getElementById('correctAnswerDisplay'); answerInput.disabled = true; feedbackSection.classList.remove('hidden'); if (isCorrect) { answerInput.classList.add('correct'); feedbackSection.className = 'feedback-section correct'; feedbackMessage.textContent = '정답입니다! 🎉'; correctAnswerDisplay.classList.add('hidden'); score++; } else { answerInput.classList.add('wrong'); feedbackSection.className = 'feedback-section wrong'; feedbackMessage.textContent = '틀렸습니다. 😓'; correctAnswerDisplay.textContent = \`정답: \${correctAnswer}\`; correctAnswerDisplay.classList.remove('hidden'); answerInput.classList.add('shake'); setTimeout(() => answerInput.classList.remove('shake'), 500); } userAnswers.push({ question: question.english, userAnswer: userAnswer, correctAnswer: correctAnswer, isCorrect: isCorrect, hintUsed: currentHintUsed }); if (currentHintUsed) { hintUsedCount++; } document.getElementById('checkBtn').classList.add('hidden'); document.getElementById('nextBtn').classList.remove('hidden'); document.getElementById('scoreDisplay').textContent = \`점수: \${score}\`; }
        function checkAnswerSimilarity(userAnswer, correctAnswer) { if (userAnswer === correctAnswer) { return true; } const cleanUser = userAnswer.replace(/\s+/g, ''); const cleanCorrect = correctAnswer.replace(/\s+/g, ''); if (cleanUser === cleanCorrect) { return true; } const possibleAnswers = correctAnswer.split(',').map(ans => ans.trim()); return possibleAnswers.some(answer => userAnswer === answer || cleanUser === answer.replace(/\s+/g, '')); }
        function nextQuestion() { currentQuestionIndex++; showQuestion(); }
        function playSound() { const question = quizData[currentQuestionIndex]; if ('speechSynthesis' in window) { const utterance = new SpeechSynthesisUtterance(question.english); utterance.lang = 'en-US'; utterance.rate = 0.8; speechSynthesis.speak(utterance); } }
        function showResults() { document.getElementById('quizScreen').classList.add('hidden'); document.getElementById('resultsScreen').classList.remove('hidden'); const totalQuestions = quizData.length; const correctAnswers = userAnswers.filter(answer => answer.isCorrect).length; const wrongAnswers = totalQuestions - correctAnswers; const accuracy = Math.round((correctAnswers / totalQuestions) * 100); document.getElementById('finalScore').textContent = \`\${correctAnswers} / \${totalQuestions}\`; document.getElementById('finalAccuracy').textContent = \`정답률: \${accuracy}%\`; document.getElementById('correctCount').textContent = correctAnswers; document.getElementById('wrongCount').textContent = wrongAnswers; document.getElementById('hintUsedCount').textContent = hintUsedCount; const resultsList = document.getElementById('resultsList'); resultsList.innerHTML = ''; userAnswers.forEach((answer, index) => { const resultItem = document.createElement('div'); resultItem.className = \`result-item \${answer.isCorrect ? 'correct' : 'wrong'}\`; let answerText = ''; if (answer.isCorrect) { answerText = \`<div class="result-answer">입력한 답: <span class="result-correct-answer">\${answer.userAnswer}</span></div>\`; } else { answerText = \`<div class="result-answer">입력한 답: <span class="result-wrong-answer">\${answer.userAnswer}</span></div><div class="result-answer">정답: <span class="result-correct-answer">\${answer.correctAnswer}</span></div>\`; } const hintBadge = answer.hintUsed ? ' <span style="background: #ffa726; color: white; padding: 2px 6px; border-radius: 10px; font-size: 10px;">힌트</span>' : ''; resultItem.innerHTML = \`<div class="result-word">\${answer.question}\${hintBadge}</div>\${answerText}\`; resultsList.appendChild(resultItem); }); logLearningData(); }
        function logLearningData() { const endTime = new Date(); const learningDurationMs = endTime.getTime() - startTime.getTime(); const learningDurationMinutes = Math.floor(learningDurationMs / 60000); const learningDurationSeconds = Math.floor((learningDurationMs % 60000) / 1000); const correctAnswers = userAnswers.filter(answer => answer.isCorrect).length; const learningLog = { learner_name: learnerName, quiz_type: "short_answer", start_time: startTime.toISOString(), end_time: endTime.toISOString(), learning_duration: \`\${learningDurationMinutes}분 \${learningDurationSeconds}초\`, learning_duration_seconds: Math.floor(learningDurationMs / 1000), total_questions: quizData.length, correct_answers: correctAnswers, accuracy: Math.round((correctAnswers / quizData.length) * 100), hint_used_count: hintUsedCount, detailed_answers: userAnswers }; console.log('=== 주관식 퀴즈 학습 로그 ==='); console.log(JSON.stringify(learningLog, null, 2)); }
        function restartQuiz() { document.getElementById('resultsScreen').classList.add('hidden'); document.getElementById('startScreen').classList.remove('hidden'); document.getElementById('learnerName').value = ''; currentQuestionIndex = 0; score = 0; userAnswers = []; hintUsedCount = 0; currentHintUsed = false; }
        document.addEventListener('keypress', function(e) { if (e.key === 'Enter') { if (e.target.id === 'learnerName') { startQuiz(); } else if (e.target.id === 'answerInput') { if (!document.getElementById('checkBtn').classList.contains('hidden')) { checkAnswer(); } else if (!document.getElementById('nextBtn').classList.contains('hidden')) { nextQuestion(); } } } });
    </script>
</body>
</html>`;
  } else if (templateName === 'multiple-choice-quiz-template.html') {
    return `<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>객관식 영어 단어 퀴즈</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .container { background: white; border-radius: 20px; padding: 30px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); max-width: 600px; width: 100%; }
        .title { color: #333; margin-bottom: 30px; font-size: 24px; font-weight: bold; text-align: center; }
        .name-input { margin-bottom: 30px; text-align: center; }
        .name-input input { padding: 15px; border: 2px solid #e1e5e9; border-radius: 10px; font-size: 16px; width: 100%; max-width: 300px; margin-bottom: 15px; }
        .progress-section { margin-bottom: 20px; }
        .progress-info { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
        .question-counter { font-size: 14px; color: #666; }
        .progress { background: #e1e5e9; border-radius: 10px; height: 8px; overflow: hidden; }
        .progress-bar { background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%); height: 100%; transition: width 0.3s ease; }
        .question-card { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); border-radius: 15px; padding: 40px 30px; margin: 30px 0; color: white; text-align: center; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
        .question-title { font-size: 18px; margin-bottom: 20px; opacity: 0.9; }
        .english-word { font-size: 36px; font-weight: bold; margin-bottom: 20px; }
        .sound-btn { background: rgba(255,255,255,0.2); border: none; border-radius: 50%; width: 60px; height: 60px; color: white; cursor: pointer; font-size: 24px; transition: all 0.3s; }
        .sound-btn:hover { background: rgba(255,255,255,0.3); transform: scale(1.1); }
        .choices-container { margin: 30px 0; }
        .choice { display: block; width: 100%; padding: 15px 20px; margin: 10px 0; background: white; border: 2px solid #e1e5e9; border-radius: 10px; font-size: 16px; text-align: left; cursor: pointer; transition: all 0.3s; }
        .choice:hover { border-color: #4facfe; background: #f8f9ff; }
        .choice.selected { border-color: #4facfe; background: #e6f3ff; }
        .choice.correct { border-color: #4CAF50; background: #e8f5e8; color: #2e7d32; }
        .choice.wrong { border-color: #f44336; background: #ffeaea; color: #c62828; }
        .choice.disabled { cursor: not-allowed; opacity: 0.7; }
        .choice-letter { font-weight: bold; margin-right: 10px; color: #666; }
        .btn { padding: 15px 30px; border: none; border-radius: 10px; font-size: 16px; font-weight: bold; cursor: pointer; transition: all 0.3s; min-width: 120px; }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }
        .next-btn { background: #4facfe; color: white; width: 100%; margin-top: 20px; }
        .next-btn:hover:not(:disabled) { background: #3b9cfe; transform: translateY(-2px); }
        .start-btn, .retry-btn { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 40px; font-size: 18px; }
        .start-btn:hover, .retry-btn:hover { transform: translateY(-2px); }
        .results-screen { text-align: center; }
        .score { font-size: 48px; font-weight: bold; color: #4facfe; margin: 20px 0; }
        .accuracy { font-size: 24px; color: #666; margin-bottom: 30px; }
        .results-list { max-height: 400px; overflow-y: auto; margin: 30px 0; text-align: left; }
        .result-item { padding: 15px; margin: 10px 0; border-radius: 10px; border: 2px solid; }
        .result-item.correct { border-color: #4CAF50; background: #e8f5e8; }
        .result-item.wrong { border-color: #f44336; background: #ffeaea; }
        .result-word { font-size: 18px; font-weight: bold; margin-bottom: 5px; }
        .result-answer { font-size: 14px; color: #666; }
        .result-correct-answer { color: #4CAF50; font-weight: bold; }
        .result-wrong-answer { color: #f44336; }
        .buttons-container { display: flex; gap: 15px; justify-content: center; margin-top: 30px; }
        .hidden { display: none; }
        @media (max-width: 480px) { .container { padding: 20px; margin: 10px; } .english-word { font-size: 28px; } .choice { padding: 12px 15px; font-size: 14px; } .buttons-container { flex-direction: column; gap: 10px; } .btn { width: 100%; } }
    </style>
</head>
<body>
    <div class="container">
        <div id="startScreen">
            <h1 class="title">객관식 영어 단어 퀴즈</h1>
            <p style="text-align: center; color: #666; margin-bottom: 30px;">{{TOTAL_QUESTIONS}}개의 단어 문제를 풀어보세요!</p>
            <div class="name-input">
                <input type="text" id="learnerName" placeholder="이름을 입력하세요" maxlength="20"><br>
                <button class="btn start-btn" onclick="startQuiz()">퀴즈 시작</button>
            </div>
        </div>
        <div id="quizScreen" class="hidden">
            <div class="progress-section">
                <div class="progress-info"><span class="question-counter" id="questionCounter">1 / {{TOTAL_QUESTIONS}}</span></div>
                <div class="progress"><div class="progress-bar" id="progressBar"></div></div>
            </div>
            <div class="question-card">
                <div class="question-title">다음 단어의 뜻은?</div>
                <div class="english-word" id="englishWord"></div>
                <button class="sound-btn" onclick="playSound()">🔊</button>
            </div>
            <div class="choices-container" id="choicesContainer"></div>
            <button class="btn next-btn" id="nextBtn" onclick="nextQuestion()" disabled>다음 문제</button>
        </div>
        <div id="resultsScreen" class="hidden">
            <div class="results-screen">
                <h2 class="title">퀴즈 완료!</h2>
                <div class="score" id="finalScore">0 / 0</div>
                <div class="accuracy" id="finalAccuracy">정답률: 0%</div>
                <div class="results-list" id="resultsList"></div>
                <div class="buttons-container"><button class="btn retry-btn" onclick="restartQuiz()">다시 풀기</button></div>
                <p style="margin-top: 30px; color: #666; font-size: 14px;">학습 로그가 콘솔에 출력되었습니다.</p>
            </div>
        </div>
    </div>
    <script>
        const quizData = {{QUIZ_DATA}};
        let currentQuestionIndex = 0, score = 0, userAnswers = [], learnerName = '', startTime = new Date(), selectedChoice = null;
        function startQuiz() { learnerName = document.getElementById('learnerName').value.trim(); if (!learnerName) { alert('이름을 입력해주세요.'); return; } document.getElementById('startScreen').classList.add('hidden'); document.getElementById('quizScreen').classList.remove('hidden'); currentQuestionIndex = 0; score = 0; userAnswers = []; startTime = new Date(); showQuestion(); }
        function showQuestion() { if (currentQuestionIndex >= quizData.length) { showResults(); return; } const question = quizData[currentQuestionIndex]; selectedChoice = null; const progress = ((currentQuestionIndex + 1) / quizData.length) * 100; document.getElementById('progressBar').style.width = progress + '%'; document.getElementById('questionCounter').textContent = \`\${currentQuestionIndex + 1} / \${quizData.length}\`; document.getElementById('englishWord').textContent = question.english; const choicesContainer = document.getElementById('choicesContainer'); choicesContainer.innerHTML = ''; question.choices.forEach((choice, index) => { const choiceButton = document.createElement('button'); choiceButton.className = 'choice'; choiceButton.innerHTML = \`<span class="choice-letter">\${String.fromCharCode(65 + index)}.</span>\${choice}\`; choiceButton.onclick = () => selectChoice(choiceButton, choice); choicesContainer.appendChild(choiceButton); }); document.getElementById('nextBtn').disabled = true; const nextBtn = document.getElementById('nextBtn'); nextBtn.textContent = currentQuestionIndex < quizData.length - 1 ? '다음 문제' : '결과 보기'; }
        function selectChoice(choiceElement, selectedAnswer) { if (selectedChoice !== null) return; selectedChoice = selectedAnswer; const question = quizData[currentQuestionIndex]; const isCorrect = selectedAnswer === question.correctAnswer; const choices = document.querySelectorAll('.choice'); choices.forEach(choice => { choice.classList.add('disabled'); const choiceText = choice.textContent.substring(2); if (choiceText === question.correctAnswer) { choice.classList.add('correct'); } else if (choiceText === selectedAnswer && !isCorrect) { choice.classList.add('wrong'); } }); userAnswers.push({ question: question.english, selectedAnswer: selectedAnswer, correctAnswer: question.correctAnswer, isCorrect: isCorrect }); if (isCorrect) { score++; } document.getElementById('nextBtn').disabled = false; }
        function nextQuestion() { currentQuestionIndex++; showQuestion(); }
        function playSound() { const question = quizData[currentQuestionIndex]; if ('speechSynthesis' in window) { const utterance = new SpeechSynthesisUtterance(question.english); utterance.lang = 'en-US'; utterance.rate = 0.8; speechSynthesis.speak(utterance); } }
        function showResults() { document.getElementById('quizScreen').classList.add('hidden'); document.getElementById('resultsScreen').classList.remove('hidden'); const totalQuestions = quizData.length; const accuracy = Math.round((score / totalQuestions) * 100); document.getElementById('finalScore').textContent = \`\${score} / \${totalQuestions}\`; document.getElementById('finalAccuracy').textContent = \`정답률: \${accuracy}%\`; const resultsList = document.getElementById('resultsList'); resultsList.innerHTML = ''; userAnswers.forEach((answer, index) => { const resultItem = document.createElement('div'); resultItem.className = \`result-item \${answer.isCorrect ? 'correct' : 'wrong'}\`; let answerText = ''; if (answer.isCorrect) { answerText = \`<div class="result-answer">선택한 답: <span class="result-correct-answer">\${answer.selectedAnswer}</span></div>\`; } else { answerText = \`<div class="result-answer">선택한 답: <span class="result-wrong-answer">\${answer.selectedAnswer}</span></div><div class="result-answer">정답: <span class="result-correct-answer">\${answer.correctAnswer}</span></div>\`; } resultItem.innerHTML = \`<div class="result-word">\${answer.question}</div>\${answerText}\`; resultsList.appendChild(resultItem); }); logLearningData(); }
        function logLearningData() { const endTime = new Date(); const learningDurationMs = endTime.getTime() - startTime.getTime(); const learningDurationMinutes = Math.floor(learningDurationMs / 60000); const learningDurationSeconds = Math.floor((learningDurationMs % 60000) / 1000); const learningLog = { learner_name: learnerName, quiz_type: "multiple_choice", start_time: startTime.toISOString(), end_time: endTime.toISOString(), learning_duration: \`\${learningDurationMinutes}분 \${learningDurationSeconds}초\`, learning_duration_seconds: Math.floor(learningDurationMs / 1000), total_questions: quizData.length, correct_answers: score, accuracy: Math.round((score / quizData.length) * 100), detailed_answers: userAnswers }; console.log('=== 객관식 퀴즈 학습 로그 ==='); console.log(JSON.stringify(learningLog, null, 2)); }
        function restartQuiz() { document.getElementById('resultsScreen').classList.add('hidden'); document.getElementById('startScreen').classList.remove('hidden'); document.getElementById('learnerName').value = ''; currentQuestionIndex = 0; score = 0; userAnswers = []; selectedChoice = null; }
        document.getElementById('learnerName').addEventListener('keypress', function(e) { if (e.key === 'Enter') { startQuiz(); } });
    </script>
</body>
</html>`;
  } else if (templateName === 'flashcard-template.html') {
    return `<!DOCTYPE html>
<html lang="ko">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>단어 플래시 카드</title>
    <style>
        /* 기본 스타일 */
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .container { background: white; border-radius: 20px; padding: 30px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); text-align: center; max-width: 500px; width: 100%; }
        .title { color: #333; margin-bottom: 30px; font-size: 24px; font-weight: bold; }
        .name-input { margin-bottom: 30px; }
        .name-input input { padding: 15px; border: 2px solid #e1e5e9; border-radius: 10px; font-size: 16px; width: 100%; margin-bottom: 15px; }
        .card { background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%); border-radius: 15px; padding: 60px 30px; margin: 30px 0; min-height: 200px; display: flex; align-items: center; justify-content: center; flex-direction: column; color: white; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
        .word { font-size: 36px; font-weight: bold; margin-bottom: 20px; }
        .meaning { font-size: 24px; margin-bottom: 20px; }
        .sound-btn { background: rgba(255,255,255,0.2); border: none; border-radius: 50%; width: 60px; height: 60px; color: white; cursor: pointer; font-size: 24px; margin-bottom: 20px; transition: all 0.3s; }
        .sound-btn:hover { background: rgba(255,255,255,0.3); transform: scale(1.1); }
        .buttons { display: flex; gap: 20px; justify-content: center; margin-top: 30px; }
        .btn { padding: 15px 30px; border: none; border-radius: 10px; font-size: 16px; font-weight: bold; cursor: pointer; transition: all 0.3s; min-width: 100px; }
        .know-btn { background: #4CAF50; color: white; }
        .know-btn:hover { background: #45a049; transform: translateY(-2px); }
        .dont-know-btn { background: #f44336; color: white; }
        .dont-know-btn:hover { background: #da190b; transform: translateY(-2px); }
        .start-btn, .retry-btn, .finish-btn { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; padding: 15px 40px; font-size: 18px; }
        .progress { background: #e1e5e9; border-radius: 10px; height: 10px; margin: 20px 0; overflow: hidden; }
        .progress-bar { background: linear-gradient(90deg, #4facfe 0%, #00f2fe 100%); height: 100%; transition: width 0.3s ease; }
        .stats { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 30px 0; }
        .stat-card { background: #f8f9fa; padding: 20px; border-radius: 10px; text-align: center; }
        .stat-number { font-size: 32px; font-weight: bold; color: #667eea; margin-bottom: 5px; }
        .stat-label { color: #666; font-size: 14px; }
        .hidden { display: none; }
        @media (max-width: 480px) { .container { padding: 20px; margin: 10px; } .word { font-size: 28px; } .meaning { font-size: 20px; } .buttons { flex-direction: column; gap: 10px; } .btn { width: 100%; } .stats { grid-template-columns: 1fr; gap: 10px; } }
    </style>
</head>
<body>
    <div class="container">
        <div id="nameScreen">
            <h1 class="title">단어 플래시 카드</h1>
            <div class="name-input">
                <input type="text" id="learnerName" placeholder="이름을 입력하세요" maxlength="20">
                <button class="btn start-btn" onclick="startLearning()">학습 시작</button>
            </div>
        </div>
        <div id="learningScreen" class="hidden">
            <h2 class="title">단어 학습</h2>
            <div class="progress"><div class="progress-bar" id="progressBar"></div></div>
            <div id="progressText">1 / {{TOTAL_WORDS}}</div>
            <div class="card">
                <div id="wordDisplay" class="word"></div>
                <div id="meaningDisplay" class="meaning hidden"></div>
                <button id="soundBtn" class="sound-btn hidden" onclick="playSound()">🔊</button>
            </div>
            <div id="actionButtons" class="buttons hidden">
                <button class="btn know-btn" onclick="markKnown(true)">알고 있어요</button>
                <button class="btn dont-know-btn" onclick="markKnown(false)">모르겠어요</button>
            </div>
        </div>
        <div id="retryScreen" class="hidden">
            <h2 class="title">틀린 단어 복습</h2>
            <div class="stats">
                <div class="stat-card"><div class="stat-number" id="correctCount">0</div><div class="stat-label">맞힌 단어</div></div>
                <div class="stat-card"><div class="stat-number" id="wrongCount">0</div><div class="stat-label">틀린 단어</div></div>
            </div>
            <button class="btn retry-btn" onclick="startRetry()">복습 시작</button>
            <button class="btn finish-btn" onclick="finishLearning()">학습 완료</button>
        </div>
        <div id="completionScreen" class="hidden">
            <h2 class="title">학습 완료!</h2>
            <div class="stats">
                <div class="stat-card"><div class="stat-number" id="finalCorrect">0</div><div class="stat-label">맞힌 단어</div></div>
                <div class="stat-card"><div class="stat-number" id="finalAccuracy">0</div><div class="stat-label">정답률 (%)</div></div>
            </div>
            <p>학습 로그가 콘솔에 출력되었습니다.</p>
        </div>
    </div>
    <script>
        const words = {{WORD_DATA}};
        let currentIndex = 0, knownWords = [], unknownWords = [], learnerName = '', startTime = new Date(), isRetryMode = false, retryWords = [];
        function startLearning() { learnerName = document.getElementById('learnerName').value.trim(); if (!learnerName) { alert('이름을 입력해주세요.'); return; } document.getElementById('nameScreen').classList.add('hidden'); document.getElementById('learningScreen').classList.remove('hidden'); currentIndex = 0; knownWords = []; unknownWords = []; startTime = new Date(); showWord(); }
        function showWord() { const wordsToUse = isRetryMode ? retryWords : words; if (currentIndex >= wordsToUse.length) { if (isRetryMode || unknownWords.length === 0) { showCompletion(); } else { showRetryScreen(); } return; } const word = wordsToUse[currentIndex]; document.getElementById('wordDisplay').textContent = word.english; document.getElementById('meaningDisplay').classList.add('hidden'); document.getElementById('soundBtn').classList.add('hidden'); document.getElementById('actionButtons').classList.add('hidden'); updateProgress(); setTimeout(() => { document.getElementById('meaningDisplay').textContent = word.korean; document.getElementById('meaningDisplay').classList.remove('hidden'); document.getElementById('soundBtn').classList.remove('hidden'); document.getElementById('actionButtons').classList.remove('hidden'); }, 1500); }
        function playSound() { const word = (isRetryMode ? retryWords : words)[currentIndex].english; if ('speechSynthesis' in window) { const utterance = new SpeechSynthesisUtterance(word); utterance.lang = 'en-US'; utterance.rate = 0.8; speechSynthesis.speak(utterance); } }
        function markKnown(known) { const word = (isRetryMode ? retryWords : words)[currentIndex]; if (isRetryMode) { if (known) { knownWords.push(word); } else { unknownWords.push(word); } } else { if (known) { knownWords.push(word); } else { unknownWords.push(word); } } currentIndex++; showWord(); }
        function updateProgress() { const wordsToUse = isRetryMode ? retryWords : words; const progress = ((currentIndex + 1) / wordsToUse.length) * 100; document.getElementById('progressBar').style.width = progress + '%'; document.getElementById('progressText').textContent = \`\${currentIndex + 1} / \${wordsToUse.length}\`; }
        function showRetryScreen() { document.getElementById('learningScreen').classList.add('hidden'); document.getElementById('retryScreen').classList.remove('hidden'); document.getElementById('correctCount').textContent = knownWords.length; document.getElementById('wrongCount').textContent = unknownWords.length; }
        function startRetry() { isRetryMode = true; retryWords = [...unknownWords]; currentIndex = 0; unknownWords = []; document.getElementById('retryScreen').classList.add('hidden'); document.getElementById('learningScreen').classList.remove('hidden'); showWord(); }
        function finishLearning() { showCompletion(); }
        function showCompletion() { document.getElementById('learningScreen').classList.add('hidden'); document.getElementById('retryScreen').classList.add('hidden'); document.getElementById('completionScreen').classList.remove('hidden'); const totalWords = words.length; const accuracy = Math.round((knownWords.length / totalWords) * 100); document.getElementById('finalCorrect').textContent = knownWords.length; document.getElementById('finalAccuracy').textContent = accuracy; const endTime = new Date(); const learningDurationMs = endTime.getTime() - startTime.getTime(); const learningDurationMinutes = Math.floor(learningDurationMs / 60000); const learningDurationSeconds = Math.floor((learningDurationMs % 60000) / 1000); const learningLog = { learner_name: learnerName, start_time: startTime.toISOString(), end_time: endTime.toISOString(), learning_duration: \`\${learningDurationMinutes}분 \${learningDurationSeconds}초\`, learning_duration_seconds: Math.floor(learningDurationMs / 1000), known_words_count: knownWords.length, total_words: totalWords, accuracy: accuracy }; console.log('=== 학습 로그 ==='); console.log(JSON.stringify(learningLog, null, 2)); }
        document.getElementById('learnerName').addEventListener('keypress', function(e) { if (e.key === 'Enter') { startLearning(); } });
    </script>
</body>
</html>`;
  }
  return '';
};

// 폴백용 주관식 퀴즈 HTML 생성 함수
const getInlineShortAnswerQuizHTML = (words, settings) => {
  const template = getInlineTemplate('short-answer-quiz-template.html');
  const quizData = words.map(word => ({
    english: word.english,
    korean: word.korean
  }));
  const templateData = {
    QUIZ_DATA: quizData,
    TOTAL_QUESTIONS: words.length,
    SCHOOL_LEVEL: settings.schoolLevel,
    GRADE: settings.grade,
    WORD_COUNT: settings.wordCount
  };
  return replacePlaceholders(template, templateData);
};

// 폴백용 객관식 퀴즈 HTML 생성 함수
const getInlineMultipleChoiceQuizHTML = (words, settings) => {
  const template = getInlineTemplate('multiple-choice-quiz-template.html');
  const quizData = generateQuizData(words);
  const templateData = {
    QUIZ_DATA: quizData,
    TOTAL_QUESTIONS: words.length,
    SCHOOL_LEVEL: settings.schoolLevel,
    GRADE: settings.grade,
    WORD_COUNT: settings.wordCount
  };
  return replacePlaceholders(template, templateData);
};

// 폴백용 플래시카드 HTML 생성 함수
const getInlineFlashCardHTML = (words, settings) => {
  const template = getInlineTemplate('flashcard-template.html');
  const templateData = {
    WORD_DATA: words,
    TOTAL_WORDS: words.length,
    SCHOOL_LEVEL: settings.schoolLevel,
    GRADE: settings.grade,
    WORD_COUNT: settings.wordCount
  };
  return replacePlaceholders(template, templateData);
};

// 에세이 피드백 HTML 생성 함수
export const generateEssayFeedbackHTML = async (
  studentName, 
  schoolLevel, 
  grade, 
  topic, 
  essay, 
  gradingResult, 
  wasModified = false
) => {
  console.log('generateEssayFeedbackHTML 호출됨');
  console.log('에세이 피드백 데이터:', { studentName, schoolLevel, grade, topic, essay: essay?.substring(0, 100) });
  
  // 학교급 한글 변환
  const schoolLevelKorean = {
    'elementary': '초등학교',
    'middle': '중학교', 
    'high': '고등학교',
    'advanced': '심화과정'
  }[schoolLevel] || schoolLevel;

  // 총점 계산
  const totalScore = gradingResult.scores.task_completion + 
                    gradingResult.scores.content + 
                    gradingResult.scores.organization + 
                    gradingResult.scores.language_use;

  // 점수별 등급 계산
  const getGrade = (score) => {
    if (score >= 18) return 'A+';
    if (score >= 16) return 'A';
    if (score >= 14) return 'B+';
    if (score >= 12) return 'B';
    if (score >= 10) return 'C+';
    if (score >= 8) return 'C';
    return 'D';
  };

  // 점수별 스타일 클래스
  const getScoreClass = (score) => {
    if (score >= 4) return 'excellent';
    if (score >= 3) return 'good';
    return 'needs-improvement';
  };

  // 생성 날짜
  const generateDate = new Date().toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // 점수 카드 HTML 생성
  const scoreCards = Object.entries({
    task_completion: '과제 완성도',
    content: '내용',
    organization: '구성',
    language_use: '언어 사용'
  }).map(([key, title]) => {
    const score = gradingResult.scores[key];
    const scoreClass = getScoreClass(score);
    return `
      <div class="score-card ${scoreClass}">
          <div class="score-title">${title}</div>
          <div class="score-value ${scoreClass}">${score}/5</div>
      </div>
    `;
  }).join('');

  // 피드백 섹션 HTML 생성
  const feedbackSections = Object.entries({
    task_completion: '📋 과제 완성도 피드백',
    content: '💡 내용 피드백', 
    organization: '🏗️ 구성 피드백',
    language_use: '🔤 언어 사용 피드백'
  }).map(([key, title]) => `
    <div class="feedback-section">
        <div class="feedback-title">${title}</div>
        <div class="feedback-content">${gradingResult.feedback[key]}</div>
    </div>
  `).join('');

  // 조건부 요소들
  const schoolInfo = schoolLevel && grade ? 
    `<div class="info-item">
       <span class="info-label">학교급:</span>
       <span class="info-value">${schoolLevelKorean} ${grade}학년</span>
     </div>` : '';

  const topicInfo = topic ? 
    `<div class="info-item">
       <span class="info-label">에세이 주제:</span>
       <span class="info-value">${topic}</span>
     </div>` : '';

  const modificationNotice = wasModified ? 
    `<div class="modification-notice">
       ⚠️ 이 채점 결과는 AI 초기 결과에서 교사가 수정한 내용이 포함되어 있습니다.
     </div>` : '';

  const correctedSection = gradingResult.corrected_version ? 
    `<div class="corrected-section">
       <div class="feedback-title">✏️ 수정된 버전</div>
       <div class="corrected-content">${gradingResult.corrected_version}</div>
     </div>` : '';

  const templateData = {
    STUDENT_NAME: studentName,
    SCHOOL_INFO: schoolInfo,
    TOPIC_INFO: topicInfo,
    GENERATE_DATE: generateDate,
    MODIFICATION_NOTICE: modificationNotice,
    TOTAL_SCORE: totalScore,
    GRADE: getGrade(totalScore),
    ACCURACY: Math.round((totalScore/20)*100),
    SCORE_CARDS: scoreCards,
    ESSAY_CONTENT: essay,
    FEEDBACK_SECTIONS: feedbackSections,
    OVERALL_COMMENT: gradingResult.overall_comment,
    CORRECTED_SECTION: correctedSection
  };

  console.log('에세이 피드백 템플릿 데이터:', templateData);

  try {
    console.log('에세이 피드백 템플릿 로딩 시도 중...');
    const template = await loadTemplate('essay-feedback-template.html');
    console.log('에세이 피드백 템플릿 로딩 성공, 길이:', template.length);
    
    const result = replacePlaceholders(template, templateData);
    console.log('에세이 피드백 placeholder 치환 완료, 최종 길이:', result.length);
    
    return result;
  } catch (error) {
    console.error('에세이 피드백 템플릿 생성 오류:', error);
    throw error;
  }
};